import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { useConfig, useDeviceType } from '../hooks';
import { getDataFromApi } from '../lib/data-service';
import Icon from './Icon';
import Button from './Button';
import { geocode } from '../lib/location';

/**
 * Autocomplete dropdown that uses Stage to fetch google maps data instead of google provided autocomplete.
 *
 * @param {object} props Properties
 * @returns {React.ReactNode}
 */
function StageGoogleMapsAutoCompleteForm({ type, initialValue = '', onUserLocationChange }) {
  const { t } = useTranslation('dealers');
  const { isMobile } = useDeviceType();
  const { config } = useConfig();
  const timeoutRef = useRef(null);
  const searchRef = useRef('');
  const selectRef = useRef(null);
  const minSearchLength = 2;
  const defaultValue = initialValue ? { value: 'IGNORE', label: initialValue } : null;
  const [value, setValue] = useState(defaultValue);
  const [inputValue, setInputValue] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(window.crypto.randomUUID());
  const [options, setOptions] = useState(defaultValue ? [defaultValue] : []);

  function handleChange(newValue) {
    setValue(newValue);
    setInputValue(newValue.label);

    setTimeout(() => {
      if (selectRef.current.inputRef) {
        selectRef.current.inputRef.style.opacity = 1;
      }
    }, 0);

    if (newValue.value === 'IGNORE') {
      return;
    }

    setLoading(true);
    getDataFromApi(`dealers/search/${newValue.value}/`, new URLSearchParams({
      session: session,
    })).then(response => {
      setLoading(false);
      setSession(window.crypto.randomUUID());
      const place = response.body?.result?.place;
      if (place) {
        onUserLocationChange({
          ...place,
          countryCode: place.countryCode.toLowerCase(),
          type: 'user_search',
        });
      }
    }).catch(e => {
      setLoading(false);
    });
  }

  function handleInputChange(newInputValue, action) {
    if ((action.action === 'input-blur' || action.action === 'menu-close' || action.action === 'set-value') && newInputValue.length === 0) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setInputValue(newInputValue);

    if (newInputValue.length < minSearchLength) {
      setLoading(false);
      setOptions([]);
    }
    else {
      setLoading(true);

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        searchRef.current = newInputValue;

        const params = {
          search: newInputValue,
          session: session,
        };
        if (config.restrictAutocompleteToCountries.length > 0) {
          params['countries[]'] = config.restrictAutocompleteToCountries;
        }
        getDataFromApi('dealers/search/', new URLSearchParams(params)).then(response => {
          if (searchRef.current !== newInputValue) {
            return;
          }
          searchRef.current = '';

          setLoading(false);
          setOptions(response.body?.result?.suggestions || []);
        }).catch(e => {
          if (searchRef.current !== newInputValue) {
            return;
          }
          searchRef.current = '';
          setLoading(false);
          setOptions([]);
        });
      }, 300);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (value?.label === inputValue || inputValue.length === 0 || loading) {
      return;
    }

    setLoading(true);
    geocode(inputValue)
      .then(response => {
        setLoading(false);
        onUserLocationChange(response);
      })
      .catch(e => {
        setLoading(false);
      });
  }

  const formType = type === 'landing' ? 'landing' : 'inline';
  let buttonIconOnly = false;
  let buttonIcon = 'Oval-White';
  let buttonVariation = 'primary';
  if (formType === 'inline') {
    buttonIcon = isMobile ? 'Search-Primary' : 'Oval-Primary';
    buttonIconOnly = isMobile;
    buttonVariation = 'secondary';
  }

  return (
    <form onSubmit={handleSubmit} className={'autocomplete-form ' + formType} data-hj-suppress="true">
      <div className="autocomplete-field">
        {formType === 'inline' && (
          <Icon icon="Search-Black" />
        )}
        <Select
          ref={selectRef}
          value={value}
          inputValue={inputValue}
          className="autocomplete-input"
          classNamePrefix="autocomplete-input"
          placeholder={t('Enter ZIP City or address')}
          aria-label={t('Enter ZIP City or address')}
          isMulti={false}
          isClearable={false}
          options={options}
          isLoading={loading}
          loadingMessage={(data) => t('Loading options')}
          onChange={handleChange}
          onInputChange={handleInputChange}
          menuPortalTarget={document.body}
          noOptionsMessage={(data) => data.inputValue.length < minSearchLength ? t('Minimum search length').replace('%n%', minSearchLength) : t('No options found')}
          blurInputOnSelect
          controlShouldRenderValue={inputValue.length > 0}
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
            colors: {
              ...theme.colors,
              primary: '#51b848',
              primary75: '#51b848bb',
              primary50: '#51b84888',
              primary25: '#51b84833',
            },
          })}
          styles={{
            container: (baseStyles, state) => ({
              ...baseStyles,
              height: formType === 'inline' ? '100%' : '3.5rem',
              minWidth: formType === 'inline' ? 0 : (isMobile ? '100%' : '20rem'),
            }),
            control: (baseStyles, state) => ({
              ...baseStyles,
              height: '100%',
              cursor: 'text',
              border: formType === 'inline' ? 0 : baseStyles.border,
              paddingLeft: formType === 'inline' && !isMobile ? '3.5rem' : 0,
            }),
            input: (baseStyles, state) => ({
              ...baseStyles,
              opacity: 1,
            }),
            placeholder: (baseStyles, state) => ({
              ...baseStyles,
              textAlign: 'left',
              width: '100%',
              overflow: 'clip',
              whiteSpace: 'nowrap',
            }),
            singleValue: (baseStyles, state) => ({
              ...baseStyles,
              textAlign: 'left',
            }),
            indicatorSeparator: (baseStyles, state) => ({
              display: 'none',
            }),
            dropdownIndicator: (baseStyles, state) => ({
              display: 'none',
            }),
            menuPortal: (baseStyles, state) => ({
              ...baseStyles,
              zIndex: 1000,
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              textAlign: 'left',
            }),
          }}
        />
      </div>
      <Button type="submit" variation={buttonVariation} label={t('Search')} loading={loading} icon={buttonIcon} iconOnly={buttonIconOnly} />
    </form>
  );
}

StageGoogleMapsAutoCompleteForm.propTypes = {
  type: PropTypes.string,
  initialValue: PropTypes.string,
  onUserLocationChange: PropTypes.func,
};

export default StageGoogleMapsAutoCompleteForm;
